/**
 * Atlas Tea Co. — Product Card JS
 * assets/atlas-product-card.js
 *
 * Handles variant pill selection and AJAX add-to-cart for
 * snippets/atlas-card.liquid when show_variant_picker and/or
 * show_add_to_cart are enabled.
 *
 * Also handles the compact add-to-cart button on
 * snippets/atlas-square-product-card.liquid when show_add_to_cart
 * is enabled on the containing section.
 *
 * Uses event delegation on document — works for cards rendered
 * on initial page load and for cards injected later (e.g. after
 * a Minimog AJAX section refresh).
 *
 * ── Button structure ────────────────────────────────────────────────────
 *
 * atlas-card (featured card) ATC button:
 *   <button data-atlas-add-btn>
 *     <span data-atlas-add-label>Add to cart</span>
 *     <span data-atlas-price>$24.00</span>   ← hidden when sold out
 *   </button>
 *
 * atlas-square-product-card (browse card) ATC button:
 *   <button data-atlas-add-btn>Add to cart</button>   ← no inner spans
 *
 * Both button types share the same [data-atlas-add-form] / [data-atlas-add-btn]
 * attributes. The code uses a labelTarget pattern:
 *   var labelTarget = btn.querySelector('[data-atlas-add-label]') || btn;
 * This means all state text (Adding…, Added ✓, Error) targets the label
 * span when it exists, or the button directly when it does not — so both
 * card types work correctly with a single handler.
 *
 * ── Dependencies ────────────────────────────────────────────────────────
 *   Shopify.formatMoney  — bundled with Minimog; used for price display
 *                          updates after variant change.
 *   MinimogEvents        — Minimog pub/sub; used to sync the cart drawer
 *                          after add-to-cart.
 *   m-cart-drawer        — Minimog custom element; opened after a
 *                          successful add-to-cart.
 */

(function () {
  'use strict';

  if (window.__atlasProductCardInit) return;
  window.__atlasProductCardInit = true;

  /* ── Helpers ──────────────────────────────────────────── */

  /**
   * Walk up from el to find the nearest [data-atlas-product-card].
   * Returns null if not found.
   */
  function getCard(el) {
    return el.closest('[data-atlas-product-card]');
  }

  /**
   * Format a Shopify integer price (in cents) to a money string.
   * Falls back to a plain divided value if Shopify.formatMoney
   * is unavailable for any reason.
   */
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Build the price span(s) HTML for a given price + compare price.
   * Used to update [data-atlas-price] after a variant pill selection.
   *
   * When rendered inside the ATC button, sale colour is overridden
   * to inherit the button's text colour (via CSS), so the markup
   * is consistent regardless of context.
   */
  function buildPriceHTML(price, comparePrice) {
    if (comparePrice && comparePrice > price) {
      return (
        '<span class="atlas-card__price atlas-card__price--sale">' +
          formatMoney(price) +
        '</span>' +
        '<s class="atlas-card__price atlas-card__price--compare">' +
          formatMoney(comparePrice) +
        '</s>'
      );
    }
    return '<span class="atlas-card__price">' + formatMoney(price) + '</span>';
  }

  /**
   * Apply or clear a state modifier class on an ATC button.
   *
   * Both card types share the same JS handler but use different BEM roots:
   *   atlas-card         → .atlas-card__add-btn       → --loading / --added
   *   atlas-square-card  → .atlas-pr__card-atc-btn    → --loading / --added
   *
   * Adding both modifier names simultaneously is safe — only the one matching
   * the button's actual base class will find a CSS rule; the other is inert.
   *
   * @param {Element} btn   The [data-atlas-add-btn] element.
   * @param {string|null} state  'loading' | 'added' | null (clears all states).
   */
  function setButtonState(btn, state) {
    btn.classList.remove(
      'atlas-card__add-btn--loading',   'atlas-card__add-btn--added',
      'atlas-pr__card-atc-btn--loading', 'atlas-pr__card-atc-btn--added'
    );
    if (state) {
      btn.classList.add(
        'atlas-card__add-btn--' + state,
        'atlas-pr__card-atc-btn--' + state
      );
    }
  }


  /* ── Variant picker ───────────────────────────────────── */

  document.addEventListener('click', function (e) {
    var pill = e.target.closest('.atlas-card__variant-pill');
    if (!pill) return;

    var picker = pill.closest('[data-atlas-variant-picker]');
    var card   = getCard(pill);
    if (!picker || !card) return;

    var cardId     = picker.getAttribute('data-card-id');
    var targetCard = cardId ? document.getElementById(cardId) : card;
    if (!targetCard) return;

    var hiddenInput  = targetCard.querySelector('[data-atlas-variant-input]');
    var addBtn       = targetCard.querySelector('[data-atlas-add-btn]');
    var allPills     = picker.querySelectorAll('.atlas-card__variant-pill');

    var variantId    = pill.getAttribute('data-variant-id');
    var price        = parseInt(pill.getAttribute('data-variant-price'), 10);
    var comparePrice = parseInt(pill.getAttribute('data-variant-compare-price'), 10) || 0;
    var available    = pill.getAttribute('data-variant-available') === 'true';

    /* Update active pill state */
    allPills.forEach(function (p) {
      p.classList.remove('atlas-card__variant-pill--active');
    });
    pill.classList.add('atlas-card__variant-pill--active');

    /* Sync hidden cart input */
    if (hiddenInput) {
      hiddenInput.value = variantId;
    }

    /* Update price display.
       Always update the innerHTML with the correct price for the new variant.
       Only apply/remove `hidden` when the price span is inside the ATC button —
       in that context, sold-out hides the price so "Sold out" fills the button
       alone. In browse mode (price row in the card footer), always show the
       price regardless of availability. */
    var priceSpan = targetCard.querySelector('[data-atlas-price]');
    if (priceSpan) {
      priceSpan.innerHTML = buildPriceHTML(price, comparePrice);
      if (priceSpan.closest('[data-atlas-add-btn]')) {
        if (available) {
          priceSpan.removeAttribute('hidden');
        } else {
          priceSpan.setAttribute('hidden', '');
        }
      } else {
        priceSpan.removeAttribute('hidden');
      }
    }

    /* Update add-to-cart button availability and label.
       labelTarget is the inner label span if present (atlas-card),
       or the button itself (atlas-square-product-card). */
    if (addBtn) {
      var labelTarget = addBtn.querySelector('[data-atlas-add-label]') || addBtn;

      if (available) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        labelTarget.textContent = 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        labelTarget.textContent = 'Sold out';
      }
      setButtonState(addBtn, null);
    }
  });


  /* ── AJAX add-to-cart ─────────────────────────────────── */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-atlas-add-form]');
    if (!form) return;

    e.preventDefault();

    var btn          = form.querySelector('[data-atlas-add-btn]');
    var variantInput = form.querySelector('[data-atlas-variant-input]');

    if (!btn || !variantInput || btn.disabled) return;

    /* labelTarget — inner span for atlas-card, button itself for square card */
    var labelTarget  = btn.querySelector('[data-atlas-add-label]') || btn;
    var originalText = labelTarget.textContent;

    labelTarget.textContent = 'Adding\u2026';
    setButtonState(btn, 'loading');

    /*
     * Step 1: Collect which sections Minimog needs re-rendered.
     * Minimog listens for 'cart:grouped-sections' on documentElement
     * and pushes its section IDs into detail.sections synchronously.
     */
    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    /* Step 2: POST to /cart/add.js, bundling any required sections */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept':       'application/json'
      },
      body: JSON.stringify({
        id:       parseInt(variantInput.value, 10),
        quantity: 1,
        sections: sectionsToBundle
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function (response) {
        labelTarget.textContent = 'Added \u2713';
        setButtonState(btn, 'added');

        /*
         * Step 3: Emit cartUpdate so Minimog can refresh cart counts
         * and any bundled section HTML.
         */
        if (window.MinimogEvents && window.MinimogTheme) {
          window.MinimogEvents.emit(
            MinimogTheme.pubSubEvents.cartUpdate,
            { cart: response }
          );
        }

        /* Step 4: Open Minimog's cart drawer */
        var cartDrawer = document.querySelector('m-cart-drawer');
        if (cartDrawer) cartDrawer.open();

        setTimeout(function () {
          labelTarget.textContent = originalText;
          setButtonState(btn, null);
        }, 2000);
      })
      .catch(function () {
        labelTarget.textContent = 'Error \u2014 try again';
        setButtonState(btn, null);
        setTimeout(function () {
          labelTarget.textContent = originalText;
        }, 2500);
      });
  });

})();
